// Presentational building blocks of the guide, in the app's Tailwind idiom.

import { useState, type ReactNode } from 'react';
import { Phone, Copy, Check, Clock, Users } from 'lucide-react';
import { api, type Prompt, type QueueAgent, type WeeklyHours, type HolidayRange } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { useGuide, type DialTarget } from './context';
import { dayLong } from './i18n';
import { intervalsText, isOpenAt, nextChange, WEEKDAY_KEYS, type OpenState, type PresenceInfo } from './lib/callFlowStatus';
import { presenceLabel } from './lib/presence';

export function Card({ children, className = '', onClick, accent }: { children: ReactNode; className?: string; onClick?: () => void; accent?: string }) {
  const base = 'bg-white rounded-lg border border-gray-200';
  if (onClick) {
    return (
      <button type="button" onClick={onClick}
        className={`${base} text-left w-full hover:border-gray-300 hover:shadow-sm transition-shadow ${className}`}
        style={accent ? { borderLeftColor: accent, borderLeftWidth: 3 } : undefined}>
        {children}
      </button>
    );
  }
  return <div className={`${base} ${className}`}>{children}</div>;
}

export function SectionHead({ title, intro, actions }: { title: string; intro?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {intro && <p className="text-xs text-gray-500 mt-0.5">{intro}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 print:hidden">{actions}</div>}
    </div>
  );
}

export type BadgeKind = 'green' | 'red' | 'amber' | 'gray' | 'dark' | 'blue';
const BADGE: Record<BadgeKind, string> = {
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-700',
  amber: 'bg-amber-100 text-amber-800',
  gray: 'bg-gray-100 text-gray-600',
  dark: 'bg-gray-800 text-white',
  blue: 'bg-blue-100 text-blue-800',
};
export function Badge({ children, kind = 'gray', title }: { children: ReactNode; kind?: BadgeKind; title?: string }) {
  return <span title={title} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide ${BADGE[kind]}`}>{children}</span>;
}

/** "Available now" / "Not available now" / "Hours unknown" for a schedule. */
export function OpenBadge({ weekly, holidays, iana, large }: { weekly?: WeeklyHours; holidays?: HolidayRange[]; iana: string | null; large?: boolean }) {
  const { t, now } = useGuide();
  const state: OpenState = isOpenAt(weekly, holidays, iana, now);
  const kind: BadgeKind = state === 'open' ? 'green' : state === 'closed' ? 'red' : 'gray';
  const label = state === 'open' ? t('availableNow') : state === 'closed' ? t('notAvailableNow') : t('hoursUnknown');
  if (large) {
    return (
      <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-2.5 py-1 rounded-full ${BADGE[kind]}`}>
        <span className={`w-2 h-2 rounded-full ${state === 'open' ? 'bg-green-500' : state === 'closed' ? 'bg-red-500' : 'bg-gray-400'}`} />
        {label}
      </span>
    );
  }
  return <Badge kind={kind}>{label}</Badge>;
}

/** "Next change: opens Mon 08:30" — relative to the viewer's clock. */
export function NextChange({ weekly, holidays, iana }: { weekly?: WeeklyHours; holidays?: HolidayRange[]; iana: string | null }) {
  const { t, now, lang } = useGuide();
  const next = nextChange(weekly, holidays, iana, now);
  if (!next || !iana) return null;
  const sameDay = new Intl.DateTimeFormat('en-CA', { timeZone: iana, year: 'numeric', month: '2-digit', day: '2-digit' });
  const isToday = sameDay.format(now) === sameDay.format(next.at);
  const tomorrow = new Date(now.getTime() + 86400000);
  const isTomorrow = !isToday && sameDay.format(tomorrow) === sameDay.format(next.at);
  const time = new Intl.DateTimeFormat(lang === 'nl' ? 'nl-NL' : 'en-GB', { timeZone: iana, hour: '2-digit', minute: '2-digit', hour12: false }).format(next.at);
  const day = isToday ? t('today') : isTomorrow ? t('tomorrow')
    : new Intl.DateTimeFormat(lang === 'nl' ? 'nl-NL' : 'en-GB', { timeZone: iana, weekday: 'long', day: 'numeric', month: 'short' }).format(next.at);
  return <span className="text-xs text-gray-500">{t('nextChange')}: {next.to === 'open' ? t('opensAt') : t('closesAt')} {day} {time}</span>;
}

export function HoursTable({ weekly, compact }: { weekly?: WeeklyHours; compact?: boolean }) {
  const { t, lang } = useGuide();
  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${compact ? 'text-xs' : 'text-sm'}`}>
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400">
            <th className="py-1 pr-3 font-medium">{t('weekday')}</th>
            <th className="py-1 font-medium">{t('period')}</th>
          </tr>
        </thead>
        <tbody>
          {WEEKDAY_KEYS.map(d => {
            const list = weekly?.[d];
            const closed = !list || !list.length;
            return (
              <tr key={d} className="border-t border-gray-100">
                <td className="py-1 pr-3 text-gray-700">{dayLong(lang, d)}</td>
                <td className={`py-1 tabular-nums ${closed ? 'text-gray-400' : 'text-gray-900'}`}>{intervalsText(list, t('closed'), t('always'))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function HolidayList({ holidays }: { holidays?: HolidayRange[] }) {
  const { t, lang } = useGuide();
  if (!holidays?.length) return <div className="text-xs text-gray-400">{t('noHolidays')}</div>;
  const fmt = (s: string) => {
    const d = new Date(s.length === 10 ? `${s}T00:00` : s);
    if (Number.isNaN(d.getTime())) return s;
    return new Intl.DateTimeFormat(lang === 'nl' ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', ...(s.length > 10 ? { hour: '2-digit', minute: '2-digit' } : {}) }).format(d);
  };
  return (
    <ul className="text-xs text-gray-700 space-y-0.5">
      {holidays.map((h, i) => (
        <li key={i} className="flex justify-between gap-3"><span>{h.name}</span><span className="text-gray-500 tabular-nums">{fmt(h.start)}{h.end !== h.start ? ` – ${fmt(h.end)}` : ''}</span></li>
      ))}
    </ul>
  );
}

export interface KeypadOption { key: string; title: string; sub: string; targetId: string }

export function Keypad({ options, onSelect }: { options: KeypadOption[]; onSelect: (targetId: string) => void }) {
  const { accent } = useGuide();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map(o => (
        <button key={`${o.key}-${o.targetId}`} type="button" onClick={() => onSelect(o.targetId)}
          className="flex items-center gap-3 px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-left hover:border-gray-300 hover:shadow-sm">
          <span className="w-9 h-9 rounded-md flex items-center justify-center text-white font-bold text-base shrink-0" style={{ backgroundColor: accent }}>{o.key}</span>
          <span className="min-w-0">
            <span className="block text-sm font-medium text-gray-900 truncate">{o.title}</span>
            <span className="block text-[11px] text-gray-500">{o.sub}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

export function Breadcrumb({ items }: { items: Array<{ label: string; onClick?: () => void }> }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <span className="text-gray-300">›</span>}
          {it.onClick
            ? <button type="button" onClick={it.onClick} className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200">{it.label}</button>
            : <span className="px-2 py-0.5 rounded bg-gray-800 text-white">{it.label}</span>}
        </span>
      ))}
    </div>
  );
}

export function Kv({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 text-xs">
      {rows.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-gray-500">{k}</dt>
          <dd className="text-gray-900 text-right">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Stat({ value, label, onClick }: { value: number | string; label: string; onClick?: () => void }) {
  const inner = (
    <>
      <div className="text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    </>
  );
  return onClick
    ? <button type="button" onClick={onClick} className="bg-white rounded-lg border border-gray-200 p-3 text-left hover:border-gray-300">{inner}</button>
    : <div className="bg-white rounded-lg border border-gray-200 p-3">{inner}</div>;
}

export function EmptyNote({ text }: { text: string }) {
  return <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center text-xs text-gray-400">{text}</div>;
}

export function CopyButton({ text, label, doneLabel }: { text: string; label: string; doneLabel: string }) {
  const [done, setDone] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(text); } catch {
      const area = document.createElement('textarea'); area.value = text; area.style.position = 'fixed'; area.style.opacity = '0';
      document.body.appendChild(area); area.select(); try { document.execCommand('copy'); } catch { /* ignore */ } area.remove();
    }
    setDone(true); setTimeout(() => setDone(false), 1500);
  }
  return (
    <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-[11px] text-purple-700 hover:underline print:hidden">
      {done ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}{done ? doneLabel : label}
    </button>
  );
}

const PRESENCE_COLOR: Record<string, string> = {
  available: 'bg-green-500', availableidle: 'bg-green-500',
  busy: 'bg-red-500', busyidle: 'bg-red-500', inacall: 'bg-red-500', inameeting: 'bg-red-500', presenting: 'bg-red-500', donotdisturb: 'bg-red-600', focusing: 'bg-red-500',
  away: 'bg-amber-400', berightback: 'bg-amber-400',
  offline: 'bg-gray-300', outofoffice: 'bg-gray-300',
};

export function PresenceDot({ info }: { info?: PresenceInfo }) {
  const { t } = useGuide();
  const color = PRESENCE_COLOR[String(info?.availability || '').toLowerCase()] || 'bg-gray-200';
  return <span title={presenceLabel(t, info)} className={`inline-block w-2.5 h-2.5 rounded-full ring-2 ring-white ${color}`} />;
}

/** Shown wherever live availability would appear while the tenant has not
 *  enabled it. Admins get the one-click consent link. */
export function PresenceConsentHint() {
  const { t, presenceState, presenceConsentUrl, isAdmin } = useGuide();
  if (presenceState === 'consent') {
    return (
      <p className="text-[11px] text-gray-400 print:hidden">
        {t('presenceNotEnabled')}
        {isAdmin && presenceConsentUrl && (
          <> <a href={presenceConsentUrl} target="_blank" rel="noopener noreferrer" className="underline text-purple-700">{t('presenceGrant')}</a></>
        )}
      </p>
    );
  }
  if (presenceState === 'unavailable') return <p className="text-[11px] text-gray-400 print:hidden">{t('presenceUnavailable')}</p>;
  return null;
}

function initials(name: string): string {
  return name.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

/** Agents of a queue with presence, email and a call button. Names come from
 *  the directory; ids without a directory entry are shown shortened. */
export function AgentList({ agents, emptyText }: { agents: QueueAgent[] | undefined; emptyText: string }) {
  const { t, dirById, onDial, accent, presence, presenceState } = useGuide();
  if (!agents?.length) return <EmptyNote text={emptyText} />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {agents.map(a => {
        const d = dirById[a.id] || dirById[a.id.toLowerCase()];
        const name = d?.displayName || `${a.id.slice(0, 8)}…`;
        const info = presence?.get(a.id.toLowerCase());
        return (
          <div key={a.id} className="flex items-center gap-2.5 px-3 py-2 bg-white border border-gray-200 rounded-lg">
            <span className="relative shrink-0">
              <span className="w-8 h-8 rounded-full bg-purple-500 text-white text-[11px] font-semibold flex items-center justify-center">{initials(name)}</span>
              {presenceState === 'ok' && <span className="absolute -bottom-0.5 -right-0.5"><PresenceDot info={info} /></span>}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-gray-900 truncate">{name}</span>
              <span className="block text-[11px] text-gray-500 truncate">
                {presenceState === 'ok' ? presenceLabel(t, info) : (d?.email || '')}
                {a.optIn === false && <span className="ml-1 text-amber-700">· {t('optedOut')}</span>}
              </span>
            </span>
            {d && (
              <button type="button" onClick={() => onDial(d)} title={t('call')}
                className="p-1.5 rounded text-white shrink-0 print:hidden" style={{ backgroundColor: accent }}>
                <Phone className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Members of a (shared-voicemail or agents) group, fetched only when the
 *  accordion is opened — avoids a Graph call per department on page load. */
export function LazyGroupMembers({ groupId, name, email }: { groupId: string; name?: string; email?: string }) {
  const { t, onDial } = useGuide();
  const [open, setOpen] = useState(false);
  return (
    <details className="border border-gray-200 rounded-lg" onToggle={e => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary className="cursor-pointer px-3 py-2 text-sm text-gray-800 flex items-center gap-2">
        <Users className="w-3.5 h-3.5 text-gray-400" />
        <span className="font-medium">{name || t('group')}</span>
        {email && <span className="text-xs text-gray-500 truncate">· {email}</span>}
      </summary>
      {open && <GroupMembersBody groupId={groupId} name={name} onDial={onDial} />}
    </details>
  );
}

function GroupMembersBody({ groupId, name, onDial }: { groupId: string; name?: string; onDial: (d: DialTarget) => void }) {
  const { t } = useGuide();
  const { data: info } = useApi(() => (name ? Promise.resolve(null) : api.getGroupInfo(groupId).catch(() => null)), { deps: [groupId] });
  const { data: members, loading, error } = useApi(() => api.getGroupMembers(groupId), { deps: [groupId] });
  return (
    <div className="px-3 pb-3">
      {info && <div className="text-xs text-gray-500 mb-1">{info.displayName}{info.mail ? ` · ${info.mail}` : ''}</div>}
      {loading && <div className="text-xs text-gray-400">{t('loadingMembers')}</div>}
      {error && <div className="text-xs text-red-500">{t('membersFailed')}</div>}
      {members && members.length === 0 && <div className="text-xs text-gray-400">{t('noMembers')}</div>}
      {members && members.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {members.map(m => (
            <button key={m.id} type="button" onClick={() => onDial({ id: m.id, type: 'user', displayName: m.displayName, email: m.email, phoneNumber: m.phoneNumber })}
              className="text-left px-2.5 py-1.5 rounded border border-gray-100 hover:bg-gray-50 flex items-center gap-2">
              <span className="text-sm text-gray-900 truncate flex-1">{m.displayName}</span>
              {m.email && <span className="text-[11px] text-gray-400 truncate hidden sm:inline">{m.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PromptSummary({ prompt }: { prompt?: Prompt }) {
  const { t } = useGuide();
  if (!prompt || prompt.mode === 'none') return <span className="text-gray-400">—</span>;
  if (prompt.mode === 'tts') return <span>“{prompt.text}”</span>;
  return <span>{prompt.teamsFileName || t('audioFile')}</span>;
}

export function HoursIcon() { return <Clock className="w-3.5 h-3.5 text-gray-400" />; }
